import { useCallback, useEffect, useRef, useState } from 'react';
import SentryService from '@services/SentryService';

// Chrome 120+ video conferencing actions not yet in TypeScript's MediaSessionAction type
type ExtendedMediaSessionAction =
  | MediaSessionAction
  | 'enterpictureinpicture'
  | 'togglemicrophone'
  | 'togglecamera'
  | 'hangup';

/**
 * Type-safe wrapper for setActionHandler with extended actions.
 * Chrome supports these actions but TypeScript's lib.dom.d.ts hasn't caught up.
 */
function setMediaSessionHandler(
  action: ExtendedMediaSessionAction,
  handler: MediaSessionActionHandler | null,
): void {
  (navigator.mediaSession.setActionHandler as (action: string, handler: MediaSessionActionHandler | null) => void)(
    action,
    handler,
  );
}

type PipStrategy = 'document' | 'classic' | null;
export type PipSizeClass = 'compact' | 'full';

const PIP_SIZE_BREAKPOINT = 500;

export interface UsePictureInPictureReturn {
  /** Whether PiP is supported in this browser */
  isSupported: boolean;
  /** Whether PiP is currently active */
  isActive: boolean;
  /** Which strategy is in use */
  strategy: PipStrategy;
  /** Responsive size class based on PiP window width */
  sizeClass: PipSizeClass;
  /** Open PiP window */
  open: () => Promise<void>;
  /** Close PiP window */
  close: () => Promise<void>;
  /** Toggle PiP */
  toggle: () => Promise<void>;
  /** For Document PiP: the Window object to portal into */
  pipWindow: Window | null;
  /** For Classic PiP: ref to attach to a hidden <video> element */
  videoRef: React.RefObject<HTMLVideoElement | null>;
}

interface MediaSessionConfig {
  /** Only register handlers when true (typically when call is connected) */
  enabled: boolean;
  onToggleMic: () => void;
  onToggleCamera: () => void;
  onHangup: () => void;
  isMicActive: boolean;
  isCameraActive: boolean;
}

interface UsePictureInPictureConfig {
  /** Media Session API integration for auto-PiP and hardware controls */
  mediaSession?: MediaSessionConfig;
  /** Called when classic PiP video track ends. Return a new track to re-attach, or null to close PiP. */
  onClassicTrackLost?: () => MediaStreamTrack | null;
}

const PIP_SIZE_KEY = 'pip-window-size';
const DEFAULT_WIDTH = 400;
const DEFAULT_HEIGHT = 300;

const supportsDocumentPiP = typeof window !== 'undefined' && 'documentPictureInPicture' in window;
const supportsClassicPiP =
  typeof document !== 'undefined' &&
  'pictureInPictureEnabled' in document &&
  document.pictureInPictureEnabled;

function getStrategy(): PipStrategy {
  if (supportsDocumentPiP) return 'document';
  if (supportsClassicPiP) return 'classic';
  return null;
}

function getSavedSize(): { width: number; height: number } {
  try {
    const saved = sessionStorage.getItem(PIP_SIZE_KEY);
    if (saved) {
      const parsed: unknown = JSON.parse(saved);
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        'width' in parsed &&
        'height' in parsed &&
        typeof (parsed as { width: unknown }).width === 'number' &&
        typeof (parsed as { height: unknown }).height === 'number'
      ) {
        return { width: (parsed as { width: number }).width, height: (parsed as { height: number }).height };
      }
    }
  } catch {
    // sessionStorage unavailable or corrupted — use defaults
  }
  return { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT };
}

function saveSize(width: number, height: number): void {
  try {
    sessionStorage.setItem(PIP_SIZE_KEY, JSON.stringify({ width, height }));
  } catch {
    // sessionStorage may be unavailable
  }
}

/**
 * Copy all stylesheets and <style> elements from source to target document.
 * Returns a MutationObserver that syncs newly injected styles (e.g. MUI CSS-in-JS).
 */
function syncStyles(sourceDoc: Document, targetDoc: Document): MutationObserver {
  // Initial copy — individual try/catch so one cross-origin failure doesn't stop the rest
  sourceDoc.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]').forEach((link) => {
    try {
      targetDoc.head.appendChild(link.cloneNode(true));
    } catch {
      // Cross-origin stylesheet — skip
    }
  });
  sourceDoc.querySelectorAll<HTMLStyleElement>('style').forEach((style) => {
    try {
      targetDoc.head.appendChild(style.cloneNode(true));
    } catch {
      // Rare — skip
    }
  });

  // Observe for dynamically injected styles (MUI Emotion injects <style> at runtime)
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (
          node instanceof HTMLStyleElement ||
          (node instanceof HTMLLinkElement && node.rel === 'stylesheet')
        ) {
          try {
            targetDoc.head.appendChild(node.cloneNode(true));
          } catch {
            // Cross-origin or detached node — skip
          }
        }
      }
    }
  });
  observer.observe(sourceDoc.head, { childList: true });
  return observer;
}

function sizeClassFromWidth(width: number): PipSizeClass {
  return width >= PIP_SIZE_BREAKPOINT ? 'full' : 'compact';
}

export function usePictureInPicture(config?: UsePictureInPictureConfig): UsePictureInPictureReturn {
  const strategy = getStrategy();
  const [isActive, setIsActive] = useState(false);
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const [sizeClass, setSizeClass] = useState<PipSizeClass>(() => sizeClassFromWidth(getSavedSize().width));
  const videoRef = useRef<HTMLVideoElement>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  // --- Document PiP ---
  const openDocumentPiP = useCallback(async () => {
    try {
      const dPiP = (
        window as unknown as {
          documentPictureInPicture: {
            requestWindow: (opts: {
            width: number;
            height: number;
            disallowReturnToOpener?: boolean;
            preferInitialWindowPlacement?: boolean;
          }) => Promise<Window>;
            window: Window | null;
          };
        }
      ).documentPictureInPicture;

      // Close any existing PiP window (ours or another app's) to avoid collision
      if (dPiP.window) {
        try {
          dPiP.window.close();
        } catch {
          // May not be our window — ignore
        }
      }

      const { width, height } = getSavedSize();
      const pipWin = await dPiP.requestWindow({
        width,
        height,
        disallowReturnToOpener: true,
        preferInitialWindowPlacement: false,
      });

      observerRef.current = syncStyles(document, pipWin.document);

      // Base reset so height: 100vh and flex: 1 work inside the PiP window
      const baseStyle = pipWin.document.createElement('style');
      baseStyle.textContent = 'html, body { margin: 0; padding: 0; height: 100%; overflow: hidden; }';
      pipWin.document.head.appendChild(baseStyle);

      // Set initial size class from actual window width
      setSizeClass(sizeClassFromWidth(pipWin.innerWidth));

      // AUDIT: This handler must ONLY clean up PiP state.
      // Never touch DailyCallService, recording, or audio.
      pipWin.addEventListener('pagehide', () => {
        // Persist window size for next open
        saveSize(pipWin.innerWidth, pipWin.innerHeight);

        observerRef.current?.disconnect();
        observerRef.current = null;
        setPipWindow(null);
        setIsActive(false);
      });

      setPipWindow(pipWin);
      setIsActive(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      SentryService.addBreadcrumb({
        category: 'pip',
        message: `requestWindow failed: ${message}`,
        level: 'warning',
      });

      // Reset state — the call continues without PiP
      setPipWindow(null);
      setIsActive(false);

      // Do NOT throw — PiP failure is non-fatal
    }
  }, []);

  const closeDocumentPiP = useCallback(() => {
    if (pipWindow) {
      pipWindow.close();
      // pagehide handler will reset state
    }
  }, [pipWindow]);

  // --- Classic Video PiP ---
  const openClassicPiP = useCallback(async () => {
    if (!videoRef.current) return;
    try {
      await videoRef.current.requestPictureInPicture();
      setIsActive(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      SentryService.addBreadcrumb({
        category: 'pip.classic',
        message: `requestPictureInPicture failed: ${message}`,
        level: 'warning',
      });
    }
  }, []);

  const closeClassicPiP = useCallback(async () => {
    if (document.pictureInPictureElement) {
      try {
        await document.exitPictureInPicture();
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        SentryService.addBreadcrumb({
          category: 'pip.classic',
          message: `exitPictureInPicture failed: ${message}`,
          level: 'warning',
        });
      }
    }
  }, []);

  // Listen for classic PiP exit
  useEffect(() => {
    if (strategy !== 'classic') return;
    const handleExit = () => setIsActive(false);
    document.addEventListener('leavepictureinpicture', handleExit);
    return () => document.removeEventListener('leavepictureinpicture', handleExit);
  }, [strategy]);

  // --- Classic PiP stale track handling ---
  useEffect(() => {
    if (strategy !== 'classic' || !isActive) return;
    const video = videoRef.current;
    if (!video) return;

    const handleVideoError = () => {
      SentryService.addBreadcrumb({
        category: 'pip.classic',
        message: 'Video element error — closing PiP',
        level: 'warning',
      });
      closeClassicPiP();
    };

    const handleTrackEnded = () => {
      const newTrack = config?.onClassicTrackLost?.();
      if (newTrack) {
        video.srcObject = new MediaStream([newTrack]);
      } else {
        closeClassicPiP();
      }
    };

    video.addEventListener('error', handleVideoError);

    const track = video.srcObject instanceof MediaStream
      ? video.srcObject.getVideoTracks()[0]
      : undefined;
    track?.addEventListener('ended', handleTrackEnded);

    return () => {
      video.removeEventListener('error', handleVideoError);
      track?.removeEventListener('ended', handleTrackEnded);
    };
    // Re-run when the video source changes (track replacement)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategy, isActive, closeClassicPiP, config?.onClassicTrackLost]);

  // --- Unified API ---
  const open = useCallback(async () => {
    if (strategy === 'document') await openDocumentPiP();
    else if (strategy === 'classic') await openClassicPiP();
  }, [strategy, openDocumentPiP, openClassicPiP]);

  const close = useCallback(async () => {
    if (strategy === 'document') closeDocumentPiP();
    else if (strategy === 'classic') await closeClassicPiP();
  }, [strategy, closeDocumentPiP, closeClassicPiP]);

  const toggle = useCallback(async () => {
    if (isActive) await close();
    else await open();
  }, [isActive, open, close]);

  // --- Auto-close on tab return ---
  // Design choice: auto-close fires for ALL PiP windows (manual and auto-triggered),
  // matching Google Meet behavior. When the provider returns to the encounter tab,
  // the full CallView is visible — PiP is redundant. If the provider stays on the
  // same tab (scrolls, opens a panel), visibilitychange never fires, so PiP stays open.
  useEffect(() => {
    if (!isActive) return;

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        close();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [isActive, close]);

  // --- PiP window resize tracking (size class) ---
  useEffect(() => {
    if (!pipWindow) return;

    let rafId: number;
    const handleResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setSizeClass(sizeClassFromWidth(pipWindow.innerWidth));
      });
    };

    pipWindow.addEventListener('resize', handleResize);
    return () => {
      pipWindow.removeEventListener('resize', handleResize);
      cancelAnimationFrame(rafId);
    };
  }, [pipWindow]);

  // --- Media Session API integration (auto-PiP + hardware controls) ---
  const mediaSession = config?.mediaSession;
  const mediaSessionEnabled = mediaSession?.enabled ?? false;

  // Store handler refs to avoid stale closures in mediaSession effect
  const onToggleMicRef = useRef(mediaSession?.onToggleMic);
  const onToggleCameraRef = useRef(mediaSession?.onToggleCamera);
  const onHangupRef = useRef(mediaSession?.onHangup);
  onToggleMicRef.current = mediaSession?.onToggleMic;
  onToggleCameraRef.current = mediaSession?.onToggleCamera;
  onHangupRef.current = mediaSession?.onHangup;

  useEffect(() => {
    if (!mediaSessionEnabled) return;
    if (!('mediaSession' in navigator)) return;

    // Video conferencing auto-PiP (Chrome 120+) requires:
    // 1) enterpictureinpicture handler registered
    // 2) Actively capturing camera/mic via getUserMedia
    // 3) Site permission allowed (chrome://settings/content/automaticPictureInPicture)
    // NOTE: Do NOT set mediaSession.metadata or playbackState here — those are for
    // Chrome 134 media playback auto-PiP which has stricter rules (MEI threshold, etc.)

    // Register auto-PiP trigger. Each handler is individually try/caught because
    // unsupported actions throw — we still want the rest.
    try {
      setMediaSessionHandler('enterpictureinpicture', () => {
        if (import.meta.env.DEV) {
          console.debug('[AutoPiP] enterpictureinpicture handler invoked, isActive:', isActive);
        }
        if (!isActive) {
          open().catch((error: unknown) => {
            const message = error instanceof Error ? error.message : String(error);
            SentryService.addBreadcrumb({
              category: 'pip.auto',
              message: `Auto-PiP failed: ${message}`,
              level: 'warning',
            });
          });
        }
      });
    } catch {
      // enterpictureinpicture not supported in this browser
    }

    try {
      setMediaSessionHandler('togglemicrophone', () => {
        onToggleMicRef.current?.();
      });
    } catch {
      // togglemicrophone not supported
    }

    try {
      setMediaSessionHandler('togglecamera', () => {
        onToggleCameraRef.current?.();
      });
    } catch {
      // togglecamera not supported
    }

    try {
      setMediaSessionHandler('hangup', () => {
        onHangupRef.current?.();
      });
    } catch {
      // hangup not supported
    }

    if (import.meta.env.DEV) {
      console.debug('[AutoPiP] Media Session handlers registered', {
        hasDocumentPiP: 'documentPictureInPicture' in window,
      });
    }

    return () => {
      try { setMediaSessionHandler('enterpictureinpicture', null); } catch { /* unsupported */ }
      try { setMediaSessionHandler('togglemicrophone', null); } catch { /* unsupported */ }
      try { setMediaSessionHandler('togglecamera', null); } catch { /* unsupported */ }
      try { setMediaSessionHandler('hangup', null); } catch { /* unsupported */ }
    };
  }, [mediaSessionEnabled, isActive, open]);

  // Sync mic/camera state with Media Session (for OS-level media controls)
  useEffect(() => {
    if (!mediaSessionEnabled || !('mediaSession' in navigator)) return;
    try {
      // setMicrophoneActive/setCameraActive may not exist in older Chrome
      (navigator.mediaSession as unknown as { setMicrophoneActive: (active: boolean) => void })
        .setMicrophoneActive(mediaSession?.isMicActive ?? false);
    } catch {
      // Not supported
    }
  }, [mediaSessionEnabled, mediaSession?.isMicActive]);

  useEffect(() => {
    if (!mediaSessionEnabled || !('mediaSession' in navigator)) return;
    try {
      (navigator.mediaSession as unknown as { setCameraActive: (active: boolean) => void })
        .setCameraActive(mediaSession?.isCameraActive ?? false);
    } catch {
      // Not supported
    }
  }, [mediaSessionEnabled, mediaSession?.isCameraActive]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
      if (pipWindow) pipWindow.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isSupported: strategy !== null,
    isActive,
    strategy,
    sizeClass,
    open,
    close,
    toggle,
    pipWindow,
    videoRef,
  };
}
